import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { invoiceActions } from "../../redux/invoiceSlice";
import { Link, useNavigate } from "react-router-dom";
import { Skeleton, Button, Modal, Pagination, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import {
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  CopyOutlined,
} from "@ant-design/icons";
import { convertToNepaliDate } from "../../components/FormatDate";
const PaymentList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { invoices, loading, error } = useSelector((state) => state.invoices);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [isInvoiceOpen, setIsInvoiceOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);

  useEffect(() => {
    dispatch(invoiceActions.getInvoices());
  }, [dispatch]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleShowInvoice = (payment) => {
    setSelectedPayment(payment);
    setIsInvoiceOpen(true);
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: "Are you sure you want to delete this invoice?",
      content: "This action cannot be undone.",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          await dispatch(invoiceActions.deleteInvoice(id)).unwrap();
          message.success("Invoice deleted successfully");
        } catch (error) {
          message.error("Failed to delete invoice");
        }
      },
    });
  };

  const handleDuplicate = (payment) => {
    navigate("/dashboard/addinvoice", {
      state: {
        duplicateData: {
          tenant_id: payment.tenant_id,
          house_number: payment.house_number,
          month: payment.month,
          invoice_date: convertToNepaliDate(payment.invoice_date),
          items: payment.invoice_items,
          due: convertToNepaliDate(payment.due_at_invoice),
        },
      },
    });
  };

 const handleEdit = (payment) => {
 
  navigate("/dashboard/addinvoice", {
    state: {
      invoiceId: payment.id,
      mode: 'edit'
    },
  });
};

  // Paginate all invoices
  const paginatedInvoices = invoices?.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  return (
    <div className="w-full bg-slate-200 p-6 pb-20 rounded-lg shadow-md">
      <div className="bg-white rounded-lg p-4">
        <div className="mb-4 flex justify-between">
          <h1 className="text-xl font-semibold mb-4">List Of Invoices</h1>
          <Link to="/dashboard/addinvoice">
            <Button
              type="default"
              className="bg-green-800 text-white hover:bg-green-600"
              icon={<PlusOutlined />}
            >
              Add New Invoice
            </Button>
          </Link>
        </div>

        {loading ? (
          <div className="p-12">
            <Skeleton active />
          </div>
        ) : (
          <div className="bg-white p-4 rounded-lg overflow-x-auto">
            <table className="min-w-full border-2 border-black">
              <thead>
                <tr className="text-left">
                  <th className="py-2 px-4 border-r border-b-2 border-black">S.N</th>
                  <th className="py-2 px-4 border-r border-b-2 border-black">Invoice No</th>
                  <th className="py-2 px-4 border-r border-b-2 border-black">Date</th>
                  <th className="py-2 px-4 border-r border-b-2 border-black">House No</th>
                  <th className="py-2 px-4 border-r border-b-2 border-black">Owner Name</th>
                  <th className="py-2 px-4 border-r border-b-2 border-black">Amount</th>
                  <th className="py-2 px-4 border-r border-b-2 border-black">Status</th>
                  <th className="py-2 px-4 border-b-2 border-black">Action</th>
                </tr>
              </thead>

              <tbody>
                {paginatedInvoices && paginatedInvoices.length > 0 ? (
                  paginatedInvoices.map((payment, index) => (
                    <tr key={payment.id} className="hover:bg-gray-50 border-b border-black">
                      <td className="py-2 px-4 border-r border-black">
                        {(currentPage - 1) * pageSize + index + 1}
                      </td>
                      <td className="py-2 px-4 border-r border-black">{payment.invoice_number}</td>
                      <td className="py-2 px-4 border-r border-black">{convertToNepaliDate(payment.invoice_date)}</td>
                      <td className="py-2 px-4 border-r border-black">{payment.house_number}</td>
                      <td className="py-2 px-4 border-r border-black">{payment.user_name}</td>
                      <td className="py-2 px-4 border-r border-black">Rs. {payment.total_amount}</td>
                      <td className="py-2 px-4 border-r border-black">
                        <span
                          className={`px-2 py-1 rounded text-xs ${
                            payment.status === "Paid" || payment.status === "Fully Paid"
                              ? "bg-green-100 text-green-800"
                              : payment.status === "Partially Paid"
                              ? "bg-yellow-100 text-yellow-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {payment.status}
                        </span>
                      </td>
                      <td className="py-2 px-4 flex gap-2">
                        {/* Edit & Delete: Only show if status is "Not Paid" */}
                        {payment.status === "Not Paid" && (
                          <>
                            <Button
                              type="default"
                              className="bg-blue-800 text-white hover:bg-blue-600"
                              onClick={() => handleEdit(payment)}
                              icon={<EditOutlined />}
                              size="small"
                              title="Edit Invoice"
                            />
                            <Button
                              danger
                              onClick={() => handleDelete(payment.id)}
                              icon={<DeleteOutlined />}
                              size="small"
                              title="Delete Invoice"
                            />
                          </>
                        )}

                        {/* View Invoice */}
                        <Button
                          onClick={() => handleShowInvoice(payment)}
                          type="default"
                          className="bg-gray-200 text-gray-800 hover:bg-gray-300"
                          icon={<EyeOutlined />}
                          size="small"
                          title="View Invoice"
                        />

                        {/* Duplicate */}
                        <Button
                          icon={<CopyOutlined />}
                          onClick={() => handleDuplicate(payment)}
                          className="text-orange-600"
                          size="small"
                          title="Duplicate Invoice"
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={8} className="py-4 px-4 text-center text-gray-500">
                      No invoices found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        <Pagination
          current={currentPage}
          pageSize={pageSize}
          total={invoices?.length || 0}
          onChange={handlePageChange}
          className="mt-4 text-center"
        />

        {/* Invoice Details Modal */}
        {isInvoiceOpen && selectedPayment && (
          <Modal
            open={isInvoiceOpen}
            onCancel={() => setIsInvoiceOpen(false)}
            footer={null}
            width={800}
          >
            <div className="p-8">
              <h2 className="text-xl font-bold text-center mb-2">
                Society of Comfort SMD Awas Bayawasthapan Samiti
              </h2>
              <p className="text-center">Thaiba-14, Lalitpur</p>
              <div className="mt-8 text-sm font-mono border-gray-400 pb-2 mb-4">
                <div className="flex justify-between">
                  <p>
                    <strong>Invoice No:</strong> {selectedPayment.invoice_number}
                  </p>
                  <p>
                    <strong>House No:</strong> {selectedPayment.house_number}
                  </p>
                </div>
                <div className="flex justify-between mt-2">
                  <p>
                    <strong>Date:</strong> {convertToNepaliDate(selectedPayment.invoice_date)}
                  </p>
                  <p>
                    <strong>Month:</strong> {selectedPayment.month}
                  </p>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full bg-white">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="py-2 px-4 border">S.no</th>
                      <th className="py-2 px-4 border">Particular</th>
                      <th className="py-2 px-4 border">Quantity</th>
                      <th className="py-2 px-4 border">Rate</th>
                      <th className="py-2 px-4 border">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedPayment.invoice_items && selectedPayment.invoice_items.length > 0 ? (
                      selectedPayment.invoice_items.map((item, index) => (
                        <tr key={index}>
                          <td className="py-2 px-4 border">{index + 1}</td>
                          <td className="py-2 px-4 border">{item.particular}</td>
                          <td className="py-2 px-4 border">{item.quantity}</td>
                          <td className="py-2 px-4 border">Rs. {item.rate}</td>
                          <td className="py-2 px-4 border">Rs. {item.total}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={5} className="py-2 px-4 border text-center">
                          No items found for this invoice.
                        </td>
                      </tr>
                    )}
                    <tr className="bg-gray-100 font-bold">
                      <td colSpan={4} className="py-2 px-4 border">Amount Total</td>
                      <td className="py-2 px-4 border">Rs. {selectedPayment.total_amount}</td>
                    </tr>
                    <tr className="bg-gray-100 font-bold">
                      <td colSpan={4} className="py-2 px-4 border">Due Amount Total</td>
                      <td className="py-2 px-4 border">Rs. {selectedPayment.due_at_invoice}</td>
                    </tr>
                    <tr className="bg-gray-200 font-bold">
                      <td colSpan={4} className="py-2 px-4 border">Grand Total</td>
                      <td className="py-2 px-4 border">
                        Rs. {(Number(selectedPayment.total_amount) + Number(selectedPayment.due_at_invoice)).toFixed(2)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="mt-4 text-sm">
                <p><strong>Status:</strong> {selectedPayment.status}</p>
                <p><strong>Due Date:</strong> {convertToNepaliDate(selectedPayment.due_date)}</p>
                <p><strong>Remaining Amount:</strong> Rs. {selectedPayment.remaining_amount}</p>
              </div>
            </div>
          </Modal>
        )}
      </div>
    </div>
  );
};

export default PaymentList;